import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';

// ── Eager: everything the first screen of the homepage needs. Anything added
//    here lands in the initial bundle, so keep the list short.
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import PropertyCard from './components/PropertyCard';
import Footer from './components/Footer';
import SEO from './components/SEO';
import { JsonLd, websiteSchema } from './components/JsonLd';
import InternalLinkHub from './components/InternalLinkHub';
import Reveal from './components/Reveal';
import GlobeBackdrop from './components/GlobeBackdrop';
import WhenVisible from './components/WhenVisible';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';

// ── Lazy: split out of the initial download.
//    MapSection pulls in leaflet + react-leaflet (~150 KB) for a widget that
//    sits far below the fold, so it loads only once it is reached (WhenVisible
//    below — lazy() on its own would still fetch it during page load).
const MapSection = lazy(() => import('./components/MapSection'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));
const WhatsAppFAB = lazy(() => import('./components/WhatsAppFAB'));

// ── Lazy: one chunk per route. None of these are needed to render "/".
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const PropertyDetails = lazy(() => import('./pages/PropertyDetails'));
const ReSell = lazy(() => import('./pages/ReSell'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ApprovedListings = lazy(() => import('./pages/ApprovedListings'));
const ClientLogin = lazy(() => import('./pages/ClientLogin'));
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminListingDetail = lazy(() => import('./pages/admin/AdminListingDetail'));
const BlogListPage = lazy(() => import('./pages/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage'));
const AIChatPage = lazy(() => import('./pages/AIChatPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const AreaPage = lazy(() => import('./pages/AreaPage'));
const AreasPage = lazy(() => import('./pages/AreasPage'));
const DeveloperPage = lazy(() => import('./pages/DeveloperPage'));
const DevelopersPage = lazy(() => import('./pages/DevelopersPage'));

/** Shown while a route chunk is in flight. Full-height so the footer does not
 *  jump up and register as a layout shift. */
const RouteFallback = () => (
    <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-dark-bg)',
        color: 'var(--color-gold)',
        letterSpacing: '2px',
        textTransform: 'uppercase',
        fontSize: '1.1rem'
    }}>…</div>
);
import { fetchProjects, searchProjects } from './services/api';
import { pageCopy } from './config/pageCopy';
import { useTranslation } from 'react-i18next';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { AuthProvider } from './contexts/AuthContext';

function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        // One request for both: the map and link hub take the first 100,
        // the list shows the first 10 of those same rows. (It used to be a
        // second, sequential request for page 1 at 10 per page — the same
        // rows, one more round trip to the API before the list appeared.)
        const response = await fetchProjects(1, 100);
        const rows = Array.isArray(response?.data?.data) ? response.data.data
          : Array.isArray(response?.data) ? response.data : [];
        setAllProjects(rows);
        setProjects(rows.slice(0, 10));
      } catch (error) {
        console.error("Failed to load projects", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSearch = async (filters) => {
    setLoading(true);
    try {
      const response = await searchProjects(filters);
      if (response && response.data && Array.isArray(response.data.data)) {
        setSearchResults(response.data.data);
      } else if (response && response.data && Array.isArray(response.data)) {
        setSearchResults(response.data);
      }
    } catch (error) {
      console.error("Search failed", error);
    } finally {
      setLoading(false);
    }
  };

  const displayProjects = searchResults || projects;

  const { i18n } = useTranslation();
  const homeLang = (i18n.language || 'en').split('-')[0];
  // Same strings the prerenderer writes (src/config/pageCopy.js), so the
  // <title> does not change when React takes over.
  const homeCopy = pageCopy('home', homeLang) || pageCopy('home', 'en');

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '50px' }}>
      <SEO
        fullTitle={homeCopy.title}
        description={homeCopy.description}
        path="/"
        keywords="dubai off plan, off plan dubai, dubai real estate, buy property dubai, dubai investment, عقارات دبي, недвижимость дубай, 迪拜房产"
      />
      <JsonLd data={websiteSchema()} />
      <Header />
      <HeroSection onSearch={handleSearch} />

      {/* Everything the globe travels behind: the project cards and the
          district index. It stops where this wrapper ends — just above the
          map section. */}
      <div className="globe-stage">
      <GlobeBackdrop />

      {/* Projects Section — the globe turns behind the glass cards */}
      <section className="container projects-section" style={{ padding: '5rem 1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '3rem', flexWrap: 'wrap', gap: '20px' }}>
          <div>
            <h2 style={{ fontSize: '3rem', color: '#fff', marginBottom: '0.5rem', fontWeight: 800, textTransform: 'uppercase' }}>
              {searchResults ? (
                <>
                  {t('home.search_results') || 'Search Results'}
                  <span style={{ fontSize: '1.5rem', marginLeft: '1rem', color: 'var(--color-gold)' }}>
                    ({searchResults.length})
                  </span>
                </>
              ) : t('home.latest_launches')}
            </h2>
            <div style={{ height: '4px', width: '100px', background: 'var(--color-gold)', borderRadius: '2px' }}></div>
          </div>
          <Link
            to="/projects"
            style={{
              background: 'none',
              border: 'none',
              borderBottom: '1px solid var(--color-gold)',
              color: 'var(--color-gold)',
              cursor: 'pointer',
              fontSize: '1.1rem',
              fontWeight: 600,
              letterSpacing: '1px',
              textDecoration: 'none'
            }}
          >
            {t('home.view_all_projects')}
          </Link>
        </div>

        {loading ? (
          <div style={{
            textAlign: 'center',
            color: 'var(--color-gold)',
            fontSize: '1.5rem',
            marginTop: '4rem',
            letterSpacing: '2px',
            textTransform: 'uppercase'
          }}>{t('projects.loading')}</div>
        ) : (
          <>
            {displayProjects.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#888', marginTop: '2rem' }}>
                {t('projects.loading')}
              </div>
            ) : (
              <div className="property-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '2.5rem'
              }}>
                {displayProjects.map((p, i) => (
                  <Reveal key={p.id} className="reveal-tilt" delay={(i % 3) * 0.13}>
                    <PropertyCard project={p} />
                  </Reveal>
                ))}

                {/* View All Projects Card */}
                <Reveal className="reveal-tilt" delay={(displayProjects.length % 3) * 0.13}>
                <Link
                  to="/projects"
                  className="glass-card property-card view-all-card"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Decorative circles */}
                  <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '150px',
                    height: '150px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(212, 175, 55, 0.15) 0%, transparent 70%)',
                    pointerEvents: 'none'
                  }}></div>
                  <div style={{
                    position: 'absolute',
                    bottom: '-30px',
                    left: '-30px',
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(212, 175, 55, 0.1) 0%, transparent 70%)',
                    pointerEvents: 'none'
                  }}></div>

                  {/* Icon */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--color-gold) 0%, #b8860b 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem',
                    boxShadow: '0 10px 30px rgba(212, 175, 55, 0.4)'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0a0a0f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                  </div>

                  {/* Text */}
                  <h3 style={{
                    color: 'var(--color-gold)',
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    marginBottom: '0',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    textAlign: 'center'
                  }}>
                    {t('home.view_all_projects')}
                  </h3>
                </Link>
                </Reveal>
              </div>
            )}
          </>
        )}
      </section>

      {/* District index — links the homepage's crawl equity into the project
          pages that are otherwise reachable only from the sitemap. */}
      <InternalLinkHub projects={allProjects} />
      </div>

      {/* Interactive Map — leaflet is fetched only when this point is reached */}
      <WhenVisible minHeight="420px" active={!loading}>
        <Suspense fallback={<div style={{ minHeight: '420px' }} />}>
          <MapSection projects={allProjects} />
        </Suspense>
      </WhenVisible>

      {/* Footer */}
      <Footer />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  // The AI page has its own composer pinned to the bottom — the floating
  // WhatsApp button would sit on top of it.
  const isAI = location.pathname.startsWith('/ai');
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/project/:id" element={<PropertyDetails />} />
        <Route path="/re-sell" element={<ProtectedRoute><ReSell /></ProtectedRoute>} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/areas" element={<AreasPage />} />
        <Route path="/area/:slug" element={<AreaPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/developer/:slug" element={<DeveloperPage />} />
        <Route path="/approved-listings" element={<ApprovedListings />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        {/* AI advisor */}
        <Route path="/ai" element={<AIChatPage />} />
        {/* Client Portal */}
        <Route path="/client/login" element={<ClientLogin />} />
        <Route path="/my-listings" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
        {/* Admin Panel */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
        <Route path="/admin/listing/:id" element={<AdminProtectedRoute><AdminListingDetail /></AdminProtectedRoute>} />
      </Routes>
      </Suspense>
      <Suspense fallback={null}>
        {!isAdmin && <CookieConsent />}
        {!isAdmin && !isAI && <WhatsAppFAB />}
      </Suspense>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <AppRoutes />
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
